import { useState, useMemo, useRef, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Treemap } from "recharts";

// ─── Demo Data ────────────────────────────────────────────────────────────────
// Each row = one SKU + factory line. current_allocation_pct sums to 100 per sku_id.
const DEMO_ROWS = [
  // SKU-001: 70% Factory A, 30% Factory B
  { sku_id:"SKU-001", sku_name:"Alpine Shell Jacket",  category:"Outerwear", fiscal_year:2024, factory:"Factory A", country:"China",   current_allocation_pct:70, units_sold:12000, net_selling_price:210, manufacturing_cost:60,  freight_cost:21, tariff_rate_percent:7.5  },
  { sku_id:"SKU-001", sku_name:"Alpine Shell Jacket",  category:"Outerwear", fiscal_year:2024, factory:"Factory B", country:"Vietnam", current_allocation_pct:30, units_sold:12000, net_selling_price:210, manufacturing_cost:68,  freight_cost:14, tariff_rate_percent:2    },
  // SKU-002: 100% Factory A
  { sku_id:"SKU-002", sku_name:"Tech Fleece Hoodie",   category:"Fleece",    fiscal_year:2024, factory:"Factory A", country:"China",   current_allocation_pct:100,units_sold:18000, net_selling_price:120, manufacturing_cost:30,  freight_cost:12, tariff_rate_percent:7.5  },
  // SKU-003: 50% Factory B, 50% Factory C
  { sku_id:"SKU-003", sku_name:"Trail Pant",           category:"Bottoms",   fiscal_year:2024, factory:"Factory B", country:"Vietnam", current_allocation_pct:50, units_sold:15000, net_selling_price:140, manufacturing_cost:40,  freight_cost:13, tariff_rate_percent:12   },
  { sku_id:"SKU-003", sku_name:"Trail Pant",           category:"Bottoms",   fiscal_year:2024, factory:"Factory C", country:"Mexico",  current_allocation_pct:50, units_sold:15000, net_selling_price:140, manufacturing_cost:36,  freight_cost:10, tariff_rate_percent:5    },
  // SKU-004: 100% Factory A
  { sku_id:"SKU-004", sku_name:"Performance Tee",      category:"Tops",      fiscal_year:2024, factory:"Factory A", country:"China",   current_allocation_pct:100,units_sold:42000, net_selling_price:48,  manufacturing_cost:11,  freight_cost:5,  tariff_rate_percent:7.5  },
  // SKU-005: 100% Factory C
  { sku_id:"SKU-005", sku_name:"Climbing Chalk Bag",   category:"Accessories",fiscal_year:2024,factory:"Factory C", country:"Mexico",  current_allocation_pct:100,units_sold:22000, net_selling_price:32,  manufacturing_cost:7,   freight_cost:2,  tariff_rate_percent:2    },
  // SKU-006: 60% Factory A, 40% Factory B
  { sku_id:"SKU-006", sku_name:"Down Vest",            category:"Outerwear", fiscal_year:2024, factory:"Factory A", country:"China",   current_allocation_pct:60, units_sold:8000,  net_selling_price:160, manufacturing_cost:48,  freight_cost:16, tariff_rate_percent:7.5  },
  { sku_id:"SKU-006", sku_name:"Down Vest",            category:"Outerwear", fiscal_year:2024, factory:"Factory B", country:"Vietnam", current_allocation_pct:40, units_sold:8000,  net_selling_price:160, manufacturing_cost:54,  freight_cost:11, tariff_rate_percent:2    },
  // SKU-007: 100% Factory B
  { sku_id:"SKU-007", sku_name:"Hiking Shorts",        category:"Bottoms",   fiscal_year:2024, factory:"Factory B", country:"Vietnam", current_allocation_pct:100,units_sold:25000, net_selling_price:58,  manufacturing_cost:14,  freight_cost:5,  tariff_rate_percent:2    },
  // SKU-008: 80% Factory A, 20% Factory C
  { sku_id:"SKU-008", sku_name:"Base Layer Top",       category:"Tops",      fiscal_year:2024, factory:"Factory A", country:"China",   current_allocation_pct:80, units_sold:30000, net_selling_price:55,  manufacturing_cost:13,  freight_cost:5,  tariff_rate_percent:7.5  },
  { sku_id:"SKU-008", sku_name:"Base Layer Top",       category:"Tops",      fiscal_year:2024, factory:"Factory C", country:"Mexico",  current_allocation_pct:20, units_sold:30000, net_selling_price:55,  manufacturing_cost:11,  freight_cost:3,  tariff_rate_percent:2    },
];

function splitCSVLine(line) {
  const vals = [];
  let cur = "", inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuotes = !inQuotes; }
    else if (ch === ',' && !inQuotes) { vals.push(cur.trim()); cur = ""; }
    else { cur += ch; }
  }
  vals.push(cur.trim());
  return vals;
}

function parseCSV(text) {
  const lines = text.trim().split("\n").map(l => l.replace(/\r$/, ""));
  const hdrs = splitCSVLine(lines[0]).map(h => h.toLowerCase().replace(/ /g,"_"));
  const REQUIRED_COLS = ["sku_id","factory","country","current_allocation_pct","units_sold","net_selling_price","manufacturing_cost","freight_cost","tariff_rate_percent"];
  const missing = REQUIRED_COLS.filter(c => !hdrs.includes(c));
  if (missing.length > 0) {
    alert(`CSV is missing required columns: ${missing.join(", ")}`);
    return [];
  }
  return lines.slice(1).filter(l => l.trim()).map(line => {
    const vals = splitCSVLine(line);
    const obj = {};
    hdrs.forEach((h,i) => { const raw = vals[i] || ""; const n = parseFloat(raw); obj[h] = isNaN(n) ? raw : n; });
    return obj;
  }).filter(r => r.sku_id);
}

// ─── Core calculation helpers ─────────────────────────────────────────────────

// Given raw rows, compute per-line economics
function enrichLine(r, allocOverride=null) {
  const alloc = (allocOverride !== null ? allocOverride : r.current_allocation_pct) / 100;
  const units = r.units_sold * alloc;
  const tc = r.manufacturing_cost * (r.tariff_rate_percent / 100);
  const landed = r.manufacturing_cost + r.freight_cost + tc;
  const gp = (r.net_selling_price - landed) * units;
  const gpPct = r.net_selling_price > 0 ? (r.net_selling_price - landed) / r.net_selling_price * 100 : 0;
  return { ...r, alloc, allocUnits: units, tc, landed, gp, gpPct, marginPct: gpPct };
}

// Roll up lines per SKU into a blended SKU row
function blendSKU(lines) {
  if (!lines.length) return null;
  const ref = lines[0];
  const totalUnits = lines.reduce((s,l) => s + l.allocUnits, 0);
  const totalGP    = lines.reduce((s,l) => s + l.gp, 0);
  const blendedLanded = totalUnits > 0 ? lines.reduce((s,l) => s + l.landed * l.allocUnits, 0) / totalUnits : 0;
  const blendedMargin = ref.net_selling_price > 0 ? (ref.net_selling_price - blendedLanded) / ref.net_selling_price * 100 : 0;
  const factories = [...new Set(lines.map(l => l.factory))];
  const countries = [...new Set(lines.map(l => l.country))];
  const isSingleFactory = factories.length === 1;
  const countryAllocMap = {};
  lines.forEach(l => { countryAllocMap[l.country] = (countryAllocMap[l.country] || 0) + l.alloc * 100; });
  const topCountryEntry = Object.entries(countryAllocMap).sort((a,b) => b[1]-a[1])[0];
  const topCountryName = topCountryEntry ? topCountryEntry[0] : "—";
  const topCountryAlloc = topCountryEntry ? topCountryEntry[1] : 0;
  return {
    sku_id: ref.sku_id, sku_name: ref.sku_name, category: ref.category || "—",
    fiscal_year: ref.fiscal_year, net_selling_price: ref.net_selling_price,
    units_sold: ref.units_sold, totalUnits, totalGP, blendedLanded,
    blendedMargin, factories, countries, isSingleFactory,
    topCountryName, topCountryAlloc, countryAllocMap, lines,
  };
}

// Risk score for a blended SKU
function skuRisk(sku) {
  const topCntryRisk = Math.min(sku.topCountryAlloc * 0.35, 35);
  const concRisk     = sku.isSingleFactory ? 25 : 10;
  const marginRisk   = sku.blendedMargin < 20 ? 30 : sku.blendedMargin < 35 ? 15 : 5;
  return Math.min(Math.round(topCntryRisk + concRisk + marginRisk), 100);
}

// ─── Constants ────────────────────────────────────────────────────────────────
const GOLD="#C8974A", DARK="#111210", CARD="#1A1916", BORDER="#272420";
const RED="#C0392B", ORANGE="#D4821A", GREEN="#2E7D52", BLUE="#3A6B8A";
const FAC_COLORS=["#3A6B8A","#C8974A","#4A7C59","#7B3F3F","#6B4E8B","#8B7355"];
const PALETTE=["#C8974A","#3A6B8A","#7B3F3F","#4A7C59","#6B4E8B","#8B7355"];

const fmt$=(n)=>n>=1e6?`$${(n/1e6).toFixed(2)}M`:n>=1e3?`$${(n/1e3).toFixed(0)}K`:`$${n.toFixed(0)}`;
const fmtPct=(n)=>`${n>=0?"+":""}${n.toFixed(1)}%`;
const ri=(s)=>s>=70?{l:"Critical",c:RED}:s>=45?{l:"High",c:ORANGE}:s>=25?{l:"Moderate",c:GOLD}:{l:"Low",c:GREEN};
const pctColor=(v,good="high")=>good==="high"?(v>=40?GREEN:v>=25?ORANGE:RED):(v<=10?GREEN:v<=20?ORANGE:RED);

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function MIP() {
  const [rawRows, setRawRows]   = useState([]);
  const [page, setPage]         = useState("upload");
  const [dragOver, setDragOver] = useState(false);
  const [expandedSku, setExpandedSku] = useState(null);
  const [filterCat, setFilterCat]   = useState("All");
  const [filterFac, setFilterFac]   = useState("All");
  const [filterRisk, setFilterRisk] = useState("All");
  const [searchQ, setSearchQ]       = useState("");
  const [aiText, setAiText]   = useState("");
  const [aiLoad, setAiLoad]   = useState(false);

  // Portfolio-level scenario overlays (applied on top of current state)
  const [volAdj,    setVolAdj]    = useState(0);
  const [mfgAdj,    setMfgAdj]    = useState(0);
  const [frtAdj,    setFrtAdj]    = useState(0);
  const [tariffAdj, setTariffAdj] = useState(0);

  // Scenario allocation shifts: { sku_id: { factory: newAllocPct } }
  const [allocScenario, setAllocScenario] = useState({});

  const fileRef = useRef();
  const handleFile = (file) => {
    const r = new FileReader();
    r.onload = e => { setRawRows(parseCSV(e.target.result)); setPage("portfolio"); setAiText(""); };
    r.readAsText(file);
  };
  const handleDrop = useCallback(e => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, []);

  // ─── Current State (locked baseline) ────────────────────────────────────
  const currentLines = useMemo(() => rawRows.map(r => enrichLine(r)), [rawRows]);
  const currentSKUs  = useMemo(() => {
    const grouped = {};
    currentLines.forEach(l => { if(!grouped[l.sku_id]) grouped[l.sku_id]=[]; grouped[l.sku_id].push(l); });
    return Object.values(grouped).map(blendSKU).filter(Boolean);
  }, [currentLines]);

  // ─── Scenario State (current + overlays) ────────────────────────────────
  const scenarioLines = useMemo(() => rawRows.map(r => {
    const scenAlloc = allocScenario[r.sku_id]?.[r.factory];
    const allocPct  = scenAlloc !== undefined ? scenAlloc : r.current_allocation_pct;
    const mfg    = r.manufacturing_cost * (1 + mfgAdj/100);
    const frt    = r.freight_cost * (1 + frtAdj/100);
    const tariff = Math.max(0, r.tariff_rate_percent + tariffAdj);
    const tc     = mfg * (tariff/100);
    const landed = mfg + frt + tc;
    const alloc  = allocPct / 100;
    const units  = r.units_sold * (1 + volAdj/100) * alloc;
    const gp     = (r.net_selling_price - landed) * units;
    const gpPct  = r.net_selling_price > 0 ? (r.net_selling_price - landed) / r.net_selling_price * 100 : 0;
    return { ...r, alloc, allocPct, allocUnits: units, adjMfg: mfg, adjFrt: frt, adjTariff: tariff, tc, landed, gp, gpPct, marginPct: gpPct };
  }), [rawRows, allocScenario, volAdj, mfgAdj, frtAdj, tariffAdj]);

  const scenarioSKUs = useMemo(() => {
    const grouped = {};
    scenarioLines.forEach(l => { if(!grouped[l.sku_id]) grouped[l.sku_id]=[]; grouped[l.sku_id].push(l); });
    return Object.values(grouped).map(blendSKU).filter(Boolean);
  }, [scenarioLines]);

  // ─── Portfolio metrics ───────────────────────────────────────────────────
  const baseGP    = currentSKUs.reduce((s,r) => s+r.totalGP, 0);
  const scenGP    = scenarioSKUs.reduce((s,r) => s+r.totalGP, 0);
  const baseRev   = currentLines.reduce((s,l) => s+l.net_selling_price*l.allocUnits, 0);
  const scenRev   = scenarioLines.reduce((s,l) => s+l.net_selling_price*l.allocUnits, 0);
  const gpDelta   = scenGP - baseGP;
  const gpDeltaPct= baseGP > 0 ? gpDelta/baseGP*100 : 0;
  const hasScenario = volAdj!==0||mfgAdj!==0||frtAdj!==0||tariffAdj!==0||Object.keys(allocScenario).length>0;

  // Top country exposure: weighted by GP
  const countryGPMap = currentLines.reduce((acc,l)=>{ acc[l.country]=(acc[l.country]||0)+l.gp; return acc; },{});
  const topCntryGPEntry = Object.entries(countryGPMap).sort((a,b)=>b[1]-a[1])[0];
  const topCntryGPName = topCntryGPEntry ? topCntryGPEntry[0] : "—";
  const topCntryExposure = baseGP > 0 ? (topCntryGPEntry ? topCntryGPEntry[1] : 0)/baseGP*100 : 0;

  // Factory concentration
  const facGP = currentLines.reduce((acc,l)=>{ acc[l.factory]=(acc[l.factory]||0)+l.gp; return acc; },{});
  const topFac = Object.entries(facGP).sort((a,b)=>b[1]-a[1])[0];
  const topFacShare = topFac && baseGP > 0 ? topFac[1]/baseGP*100 : 0;

  // SKU concentration
  const sortedSKUs = [...currentSKUs].sort((a,b)=>b.totalGP-a.totalGP);
  const top5GP = sortedSKUs.slice(0,5).reduce((s,r)=>s+r.totalGP,0);
  const top5Share = baseGP>0 ? top5GP/baseGP*100 : 0;

  // Tariff +10 stress
  const tariff10GP = currentLines.reduce((s,l)=>{ const tc2=l.manufacturing_cost*((l.tariff_rate_percent+10)/100); const l2=l.manufacturing_cost+l.freight_cost+tc2; return s+(l.net_selling_price-l2)*l.allocUnits; },0);
  const tariff10Chg = baseGP>0 ? (tariff10GP-baseGP)/baseGP*100 : 0;
  const stressGP = currentLines.reduce((s,l)=>{ const m=l.manufacturing_cost*1.1; const f=l.freight_cost*1.15; const tc=m*(l.tariff_rate_percent/100); const ld=m+f+tc; return s+(l.net_selling_price-ld)*l.allocUnits*0.9; },0);
  const stressChg = baseGP>0 ? (stressGP-baseGP)/baseGP*100 : 0;

  // Unique factories, categories
  const allFactories = [...new Set(rawRows.map(r=>r.factory))].sort();
  const allCategories = [...new Set(rawRows.map(r=>r.category||"—"))].sort();

  // Country GP mix
  const countryGP = currentLines.reduce((acc,l)=>{ acc[l.country]=(acc[l.country]||0)+l.gp; return acc; },{});
  const cntryPie = Object.entries(countryGP).map(([name,val])=>({name,value:parseFloat((val/baseGP*100).toFixed(1))}));

  // Factory GP bar
  const facBar = Object.entries(facGP).map(([name,gp])=>({name,gp})).sort((a,b)=>b.gp-a.gp);

  // Flags
  const flags = [];
  if(top5Share>70)   flags.push({msg:`Top 5 SKUs represent ${top5Share.toFixed(0)}% of GP — high concentration risk.`});
  if(topFacShare>60) flags.push({msg:`${topFac?.[0]} accounts for ${topFacShare.toFixed(0)}% of GP — single factory dependency.`});
  if(topCntryExposure>50) flags.push({msg:`${topCntryGPName} represents ${topCntryExposure.toFixed(0)}% of GP — elevated country concentration risk.`});
  const multiAllocSKUs = currentSKUs.filter(s=>!s.isSingleFactory).length;
  if(multiAllocSKUs>0) flags.push({msg:`${multiAllocSKUs} SKU${multiAllocSKUs>1?"s":""} split across multiple factories — allocation shift opportunities available.`});

  // ─── Filtered SKU list ───────────────────────────────────────────────────
  const filteredSKUs = useMemo(() => {
    let skus = scenarioSKUs;
    if(filterCat !== "All") skus = skus.filter(s=>s.category===filterCat);
    if(filterFac !== "All") skus = skus.filter(s=>s.factories.includes(filterFac));
    if(filterRisk !== "All") {
      skus = skus.filter(s=>{
        const r=ri(skuRisk(s));
        return filterRisk==="Critical"?r.l==="Critical":filterRisk==="High"?r.l==="High":filterRisk==="Moderate"?r.l==="Moderate":r.l==="Low";
      });
    }
    if(searchQ) skus = skus.filter(s=>s.sku_name.toLowerCase().includes(searchQ.toLowerCase())||s.sku_id.toLowerCase().includes(searchQ.toLowerCase()));
    return skus.sort((a,b)=>b.totalGP-a.totalGP);
  }, [scenarioSKUs, filterCat, filterFac, filterRisk, searchQ]);

  // ─── Scenario alloc helpers ──────────────────────────────────────────────
  const getScenAlloc = (skuId, factory, fallback) => {
    return allocScenario[skuId]?.[factory] !== undefined ? allocScenario[skuId][factory] : fallback;
  };
  const setScenAlloc = (skuId, lines, factoryChanged, newPct) => {
    // When you move one factory, distribute the remainder proportionally across others
    const otherLines = lines.filter(l=>l.factory!==factoryChanged);
    const remainder = 100 - newPct;
    const otherTotal = otherLines.reduce((s,l)=>s+(allocScenario[skuId]?.[l.factory]??l.current_allocation_pct),0);
    const newAllocs = { ...(allocScenario[skuId]||{}) };
    newAllocs[factoryChanged] = newPct;
    if(otherLines.length === 1) {
      newAllocs[otherLines[0].factory] = remainder;
    } else if(otherTotal > 0) {
      otherLines.forEach(l => {
        const cur = allocScenario[skuId]?.[l.factory] ?? l.current_allocation_pct;
        newAllocs[l.factory] = Math.round(cur / otherTotal * remainder);
      });
    }
    setAllocScenario(prev => ({ ...prev, [skuId]: newAllocs }));
  };
  const resetSkuScenario = (skuId) => {
    setAllocScenario(prev => { const n={...prev}; delete n[skuId]; return n; });
  };

  // ─── AI ──────────────────────────────────────────────────────────────────
  const generateAI = async () => {
    setAiLoad(true); setAiText("");
    const skuSummary = sortedSKUs.slice(0,10).map(s=>`${s.sku_name} [${s.factories.join("/")}] GP:${fmt$(s.totalGP)} margin:${s.blendedMargin.toFixed(1)}% risk:${skuRisk(s)}/100`).join("; ");
    const prompt = `You are a supply chain risk analyst for Blacksmith International. Portfolio: ${rawRows.length} lines, ${currentSKUs.length} SKUs across ${allFactories.length} factories. Total GP: ${fmt$(baseGP)}. Top country exposure: ${topCntryGPName} at ${topCntryExposure.toFixed(0)}% of GP. Top factory concentration: ${topFacShare.toFixed(0)}%. Tariff +10pt impact: ${tariff10Chg.toFixed(1)}%. Stress scenario: ${stressChg.toFixed(1)}%. Multi-factory SKUs: ${multiAllocSKUs}. Top SKUs: ${skuSummary}.${hasScenario?` Scenario GP delta: ${fmtPct(gpDeltaPct)}.`:""} Provide: (1) 3 most critical risks with data, (2) top 3 allocation optimization opportunities, (3) 3-4 specific action recommendations. CEO audience. Be direct.`;
    try {
      const res = await fetch("/api/analyze",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1200,messages:[{role:"user",content:prompt}]})});
      if (!res.ok) { const errData = await res.json().catch(()=>({})); throw new Error(errData.error || `API returned ${res.status}`); }
      const j = await res.json();
      setAiText(j.content?.[0]?.text||"Unable to generate.");
    } catch(err) { setAiText(`Error — ${err.message || "please retry."}`); }
    setAiLoad(false);
  };

  // ─── CSS ─────────────────────────────────────────────────────────────────
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    body,html{background:${DARK};}
    .w{font-family:'DM Sans',sans-serif;background:${DARK};min-height:100vh;color:#E2D8CC;}
    .hdr{background:#151412;border-bottom:1px solid ${BORDER};padding:14px 28px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;}
    .brand{font-family:'DM Serif Display',serif;font-size:17px;color:${GOLD};}
    .brand-sub{font-size:9px;letter-spacing:3px;color:#4A3A28;text-transform:uppercase;margin-top:2px;}
    .nav{display:flex;gap:3px;}
    .nb{background:none;border:1px solid ${BORDER};color:#6A5A40;font-family:'DM Sans',sans-serif;font-size:10px;letter-spacing:1.5px;text-transform:uppercase;padding:6px 14px;cursor:pointer;transition:all .2s;}
    .nb:hover,.nb.on{border-color:${GOLD};color:${GOLD};background:rgba(200,151,74,.06);}
    .body{padding:22px 28px;max-width:1600px;margin:0 auto;}
    .card{background:${CARD};border:1px solid ${BORDER};border-radius:2px;padding:16px 20px;}
    .sec{font-family:'DM Serif Display',serif;font-size:19px;color:#E2D8CC;margin-bottom:14px;}
    .lbl{font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#5A5040;margin-bottom:3px;}
    .stat-val{font-family:'DM Serif Display',serif;color:${GOLD};}
    .sub{font-size:10px;color:#4A3A28;margin-top:2px;}
    .up-zone{border:1px dashed #2A2520;padding:44px;text-align:center;cursor:pointer;transition:all .3s;border-radius:2px;}
    .up-zone:hover,.up-zone.drag{border-color:${GOLD};background:rgba(200,151,74,.04);}
    .btn-g{background:${GOLD};color:#0F0F0E;border:none;padding:9px 22px;font-family:'DM Sans',sans-serif;font-size:10px;font-weight:600;letter-spacing:2px;text-transform:uppercase;cursor:pointer;}
    .btn-g:hover{background:#E8B75A;}
    .btn-o{background:none;border:1px solid ${GOLD};color:${GOLD};padding:9px 22px;font-family:'DM Sans',sans-serif;font-size:10px;letter-spacing:2px;text-transform:uppercase;cursor:pointer;}
    .btn-o:hover{background:rgba(200,151,74,.1);}
    .btn-sm{background:none;border:1px solid ${BORDER};color:#6A5A40;font-family:'DM Sans',sans-serif;font-size:10px;padding:4px 10px;cursor:pointer;transition:all .2s;}
    .btn-sm:hover,.btn-sm.on{border-color:${GOLD};color:${GOLD};}
    .divider{height:1px;background:linear-gradient(90deg,transparent,${BORDER},transparent);margin:16px 0;}
    .col-tag{display:inline-block;background:#0F0F0E;border:1px solid ${BORDER};padding:2px 8px;font-size:10px;color:#6A5A40;letter-spacing:1px;margin:2px;}
    .input-f{background:#0F0F0E;border:1px solid ${BORDER};color:#E2D8CC;padding:7px 11px;font-family:'DM Sans',sans-serif;font-size:12px;outline:none;transition:border .2s;border-radius:2px;}
    .input-f:focus{border-color:${GOLD};}
    .badge{display:inline-block;padding:2px 8px;font-size:9px;font-weight:600;letter-spacing:1px;border-radius:2px;}
    .flags{background:#1C190A;border:1px solid #3A3010;border-left:3px solid ${GOLD};border-radius:2px;padding:12px 16px;margin-bottom:10px;}
    .sl-hdr{display:flex;justify-content:space-between;margin-bottom:5px;}
    .sl-name{font-size:11px;color:#9A8A70;}
    .sl-val{font-size:11px;font-weight:600;color:${GOLD};}
    input[type=range]{-webkit-appearance:none;width:100%;height:2px;background:#2A2520;outline:none;}
    input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:${GOLD};cursor:pointer;}
    .sl-scale{display:flex;justify-content:space-between;font-size:9px;color:#3A3020;margin-top:2px;}
    .sku-card{background:${CARD};border:1px solid ${BORDER};border-radius:2px;margin-bottom:6px;transition:border .2s;cursor:pointer;}
    .sku-card:hover{border-color:#3A3020;}
    .sku-card.expanded{border-color:${GOLD};}
    .sku-card.scenario-active{border-left:3px solid ${GOLD};}
    .fac-dot{display:inline-block;width:7px;height:7px;border-radius:50%;margin-right:4px;}
    .alloc-bar-wrap{height:4px;background:#1A1816;border-radius:2px;overflow:hidden;display:flex;margin-top:6px;}
    .tbl-hdr{display:grid;gap:8px;padding:7px 0;border-bottom:1px solid ${BORDER};font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#5A5040;}
    .tbl-row{display:grid;gap:8px;padding:10px 0;border-bottom:1px solid #1A1816;align-items:center;font-size:12px;color:#C0B098;}
    .scenario-banner{background:#1A1710;border:1px solid #3A3020;border-left:3px solid ${GOLD};padding:10px 16px;display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;}
    .ai-box{background:#141210;border:1px solid #2A2418;border-left:3px solid ${GOLD};padding:20px;font-size:13px;line-height:1.9;color:#C8B88A;white-space:pre-wrap;}
    .tab{background:none;border:none;border-bottom:2px solid transparent;color:#5A5040;font-family:'DM Sans',sans-serif;font-size:12px;padding:7px 14px;cursor:pointer;transition:all .2s;}
    .tab.on{color:${GOLD};border-bottom-color:${GOLD};}
  `;

  const TH=({cols,ch})=><div className="tbl-hdr" style={{gridTemplateColumns:cols}}>{ch}</div>;
  const TR=({cols,ch,style={}})=><div className="tbl-row" style={{gridTemplateColumns:cols,...style}}>{ch}</div>;

  // ─── SKU Detail Panel ─────────────────────────────────────────────────────
  const SKUPanel = ({ sku }) => {
    const scenLines = scenarioLines.filter(l=>l.sku_id===sku.sku_id);
    const baseLines = currentLines.filter(l=>l.sku_id===sku.sku_id);
    const hasAllocChange = allocScenario[sku.sku_id];
    const risk = skuRisk(sku);
    const rInfo = ri(risk);
    const baseSkuGP = baseLines.reduce((s,l)=>s+l.gp,0);
    const scenSkuGP = scenLines.reduce((s,l)=>s+l.gp,0);
    const skuGPDelta = scenSkuGP - baseSkuGP;

    return (
      <div style={{padding:"16px 20px",borderTop:`1px solid ${BORDER}`,background:"#141210"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 340px",gap:"20px"}}>

          {/* Left: factory lines */}
          <div>
            <div style={{fontSize:"10px",letterSpacing:"2px",textTransform:"uppercase",color:"#5A5040",marginBottom:"12px"}}>
              Factory Allocation — Current State vs Scenario
            </div>

            {/* Allocation bar */}
            <div className="alloc-bar-wrap" style={{height:"8px",marginBottom:"14px"}}>
              {scenLines.map((l,i)=>(
                <div key={l.factory} style={{width:`${l.allocPct}%`,background:FAC_COLORS[allFactories.indexOf(l.factory)%FAC_COLORS.length],transition:"width .3s"}}/>
              ))}
            </div>

            {scenLines.map((scenL, i) => {
              const baseL = baseLines.find(l=>l.factory===scenL.factory);
              const facColor = FAC_COLORS[allFactories.indexOf(scenL.factory)%FAC_COLORS.length];
              const curAlloc = baseL?.current_allocation_pct || 0;
              const scenAllocVal = getScenAlloc(sku.sku_id, scenL.factory, curAlloc);
              const allocChanged = scenAllocVal !== curAlloc;

              return (
                <div key={scenL.factory} style={{marginBottom:"14px",padding:"12px 14px",background:`${facColor}08`,border:`1px solid ${facColor}20`,borderRadius:"2px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                      <span className="fac-dot" style={{background:facColor}}/>
                      <span style={{fontSize:"13px",fontWeight:600,color:"#E2D8CC"}}>{scenL.factory}</span>
                      <span style={{fontSize:"11px",color:"#5A5040"}}>{scenL.country}</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                      <span style={{fontSize:"11px",color:"#5A5040"}}>Current: <span style={{color:"#9A8A70"}}>{curAlloc}%</span></span>
                      {allocChanged && <span style={{fontSize:"11px",color:GOLD}}>→ Scenario: {scenAllocVal}%</span>}
                    </div>
                  </div>

                  {/* Scenario allocation slider — only if multi-factory */}
                  {!sku.isSingleFactory && (
                    <div style={{marginBottom:"10px"}}>
                      <input type="range" min={0} max={100} step={5} value={scenAllocVal}
                        onChange={e=>setScenAlloc(sku.sku_id, baseLines, scenL.factory, +e.target.value)}/>
                      <div className="sl-scale"><span>0%</span><span>50%</span><span>100%</span></div>
                    </div>
                  )}

                  {/* Line economics */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:"8px",marginTop:"8px"}}>
                    {[
                      {l:"Mfg Cost",    v:`$${scenL.adjMfg?.toFixed(2)||scenL.manufacturing_cost.toFixed(2)}`},
                      {l:"Freight",     v:`$${(scenL.adjFrt||scenL.freight_cost).toFixed(2)}`},
                      {l:"Tariff Rate", v:`${(scenL.adjTariff||scenL.tariff_rate_percent)}%`},
                      {l:"Landed",      v:`$${scenL.landed.toFixed(2)}`},
                      {l:"Margin",      v:`${scenL.gpPct.toFixed(1)}%`, color:pctColor(scenL.gpPct)},
                    ].map(({l,v,color})=>(
                      <div key={l} style={{textAlign:"center"}}>
                        <div style={{fontSize:"9px",letterSpacing:"1.5px",textTransform:"uppercase",color:"#5A5040",marginBottom:"2px"}}>{l}</div>
                        <div style={{fontSize:"13px",color:color||"#C0B098"}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {hasAllocChange && (
              <button className="btn-sm" onClick={()=>resetSkuScenario(sku.sku_id)} style={{marginTop:"4px"}}>
                Reset Allocations for this SKU
              </button>
            )}
          </div>

          {/* Right: blended summary + GP impact */}
          <div>
            <div style={{marginBottom:"10px",fontSize:"10px",letterSpacing:"2px",textTransform:"uppercase",color:"#5A5040"}}>Blended Summary</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"14px"}}>
              {[
                {l:"Blended Landed", v:`$${sku.blendedLanded.toFixed(2)}`},
                {l:"Gross Margin",   v:`${sku.blendedMargin.toFixed(1)}%`, color:pctColor(sku.blendedMargin)},
                {l:"Total GP",       v:fmt$(sku.totalGP)},
                {l:"Risk Score",     v:`${risk}/100`, color:rInfo.c},
              ].map(({l,v,color})=>(
                <div key={l} className="card" style={{padding:"10px 14px"}}>
                  <div className="lbl">{l}</div>
                  <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"18px",color:color||GOLD,marginTop:"2px"}}>{v}</div>
                </div>
              ))}
            </div>

            {/* GP delta if scenario active */}
            {(hasScenario || hasAllocChange) && (
              <div style={{padding:"12px 14px",background:"#141210",border:`1px solid ${BORDER}`,borderLeft:`3px solid ${skuGPDelta>=0?GREEN:RED}`}}>
                <div className="lbl">Scenario GP Impact</div>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"22px",color:skuGPDelta>=0?GREEN:RED,marginTop:"2px"}}>{fmt$(skuGPDelta)}</div>
                <div style={{fontSize:"10px",color:"#4A3A28",marginTop:"2px"}}>{fmtPct(baseSkuGP>0?skuGPDelta/baseSkuGP*100:0)} vs current state</div>
              </div>
            )}

            {/* Allocation summary */}
            <div style={{marginTop:"12px"}}>
              <div style={{fontSize:"10px",letterSpacing:"2px",textTransform:"uppercase",color:"#5A5040",marginBottom:"8px"}}>Allocation Split</div>
              {scenLines.map((l,i)=>{
                const facColor=FAC_COLORS[allFactories.indexOf(l.factory)%FAC_COLORS.length];
                return(
                  <div key={l.factory} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"6px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                      <span className="fac-dot" style={{background:facColor}}/>
                      <span style={{fontSize:"12px",color:"#9A8A70"}}>{l.factory}</span>
                      <span style={{fontSize:"11px",color:"#5A5040"}}>({l.country})</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                      <div style={{width:"80px",height:"3px",background:"#1A1816",borderRadius:"2px",overflow:"hidden"}}>
                        <div style={{width:`${l.allocPct}%`,height:"100%",background:facColor,transition:"width .3s"}}/>
                      </div>
                      <span style={{fontSize:"12px",color:"#C0B098",minWidth:"30px",textAlign:"right"}}>{l.allocPct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="w">
      <style>{css}</style>

      <div className="hdr">
        <div>
          <div className="brand">⬡ Blacksmith International</div>
          <div className="brand-sub">Manufacturing Intelligence Platform · V3</div>
        </div>
        <div className="nav">
          <button className={`nb ${page==="upload"?"on":""}`} onClick={()=>setPage("upload")}>
            {rawRows.length>0?"+ Reload":"Import"}
          </button>
          {rawRows.length>0 && <>
            <button className={`nb ${page==="portfolio"?"on":""}`} onClick={()=>setPage("portfolio")}>Portfolio</button>
            <button className={`nb ${page==="skus"?"on":""}`} onClick={()=>setPage("skus")}>SKUs</button>
            <button className={`nb ${page==="ai"?"on":""}`} onClick={()=>setPage("ai")}>AI Analysis</button>
          </>}
        </div>
      </div>

      <div className="body">

        {/* ── UPLOAD ── */}
        {page==="upload" && (
          <div>
            <h1 style={{fontFamily:"'DM Serif Display',serif",fontSize:"32px",color:"#E2D8CC",marginBottom:"6px",lineHeight:1.2}}>
              Supply Chain <span style={{color:GOLD}}>Risk Intelligence</span>
            </h1>
            <p style={{fontSize:"13px",color:"#5A5040",marginBottom:"22px",maxWidth:"520px",lineHeight:1.7}}>
              One CSV. Each row is a SKU + factory combination with a <code style={{color:GOLD,fontSize:"11px"}}>current_allocation_pct</code> column. SKUs split across factories appear as multiple rows summing to 100%.
            </p>
            <div className="card" style={{marginBottom:"10px"}}>
              <div className={`up-zone ${dragOver?"drag":""}`}
                onDrop={handleDrop} onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:"24px",marginBottom:"8px",opacity:.4}}>⬡</div>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"18px",color:GOLD,marginBottom:"4px"}}>Drop CSV here</div>
                <div style={{fontSize:"11px",color:"#5A5040"}}>One row per SKU + factory line</div>
                <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e=>e.target.files[0]&&handleFile(e.target.files[0])}/>
              </div>
              <div className="divider"/>
              <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                <button className="btn-g" onClick={()=>{setRawRows(DEMO_ROWS);setPage("portfolio");setAiText("");}}>Load Demo Data</button>
                <span style={{fontSize:"11px",color:"#4A3A28"}}>8 SKUs · 3 factories · mixed allocations</span>
              </div>
            </div>
            <div className="card">
              <div style={{fontSize:"10px",letterSpacing:"2px",textTransform:"uppercase",color:"#5A5040",marginBottom:"8px"}}>Required Columns</div>
              {["sku_id","sku_name","category","fiscal_year","factory","country","current_allocation_pct","units_sold","net_selling_price","manufacturing_cost","freight_cost","tariff_rate_percent"].map(c=><span key={c} className="col-tag">{c}</span>)}
              <div style={{marginTop:"12px",fontSize:"11px",color:"#5A5040",lineHeight:1.7}}>
                <strong style={{color:"#9A8A70"}}>Example:</strong> SKU-001 split 70/30 across Factory A &amp; B = two rows with <code style={{color:GOLD}}>current_allocation_pct</code> of 70 and 30.<br/>
                SKU-002 100% at Factory A = one row with <code style={{color:GOLD}}>current_allocation_pct</code> of 100.
              </div>
            </div>
          </div>
        )}

        {/* ── PORTFOLIO ── */}
        {page==="portfolio" && rawRows.length>0 && (
          <div>
            {/* Scenario banner */}
            {hasScenario && (
              <div className="scenario-banner">
                <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                  <span style={{fontSize:"10px",letterSpacing:"2px",textTransform:"uppercase",color:GOLD}}>Scenario Active</span>
                  <span style={{fontSize:"13px",color:gpDelta>=0?GREEN:RED,fontWeight:600}}>{fmtPct(gpDeltaPct)} GP ({fmt$(gpDelta)})</span>
                  <span style={{fontSize:"11px",color:"#5A5040"}}>{fmt$(scenGP)} vs {fmt$(baseGP)} baseline</span>
                </div>
                <button className="btn-sm" onClick={()=>{setVolAdj(0);setMfgAdj(0);setFrtAdj(0);setTariffAdj(0);setAllocScenario({});}}>Reset All Scenarios</button>
              </div>
            )}

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:"14px"}}>
              <div>
                <div className="sec" style={{marginBottom:0}}>Portfolio Overview</div>
                <div style={{fontSize:"11px",color:"#4A3A28",marginTop:"3px"}}>{currentSKUs.length} SKUs · {allFactories.length} Factories · FY {rawRows[0]?.fiscal_year||"—"}</div>
              </div>
            </div>

            {/* Stat cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:"8px",marginBottom:"10px"}}>
              {[
                {l:"Total GP",            v:fmt$(hasScenario?scenGP:baseGP),       sub:hasScenario?`Baseline: ${fmt$(baseGP)}`:`Rev: ${fmt$(baseRev)}`, neg:false},
                {l:"Top 5 SKU GP Share",  v:`${top5Share.toFixed(1)}%`,             sub:"Concentration",neg:false},
                {l:"Top Factory GP Share",v:`${topFacShare.toFixed(1)}%`,           sub:topFac?.[0]||"—",neg:false},
                {l:"Top Country Exposure", v:`${topCntryExposure.toFixed(1)}%`,      sub:topCntryGPName,neg:false},
                {l:"Tariff +10 Impact",   v:fmtPct(tariff10Chg),                   sub:"GP change",neg:tariff10Chg<0},
                {l:"Stress Bundle",       v:fmtPct(stressChg),                     sub:"+10% mfg, +15% frt, -10% vol",neg:stressChg<0},
              ].map(({l,v,sub,neg})=>(
                <div key={l} className="card" style={{padding:"12px 14px"}}>
                  <div className="lbl">{l}</div>
                  <div className="stat-val" style={{fontSize:"20px",color:neg?RED:GOLD}}>{v}</div>
                  <div className="sub">{sub}</div>
                </div>
              ))}
            </div>

            {/* Risk cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr) 260px",gap:"8px",marginBottom:"10px"}}>
              {[
                {t:"SKU\nConcentration",  s:top5Share,               sub:"Top 5 SKU GP share"},
                {t:"Factory\nDependency", s:topFacShare,             sub:"Top factory GP share"},
                {t:"Top Country\nExposure", s:topCntryExposure,      sub:`${topCntryGPName} GP share`},
                {t:"Tariff\nSensitivity", s:Math.abs(tariff10Chg)*2, sub:"GP impact from +10% tariff"},
                {t:"Margin\nResilience",  s:Math.abs(stressChg)*1.5, sub:"Stress bundle GP impact"},
              ].map(({t,s,sub})=>{
                const r=ri(s);
                return(
                  <div key={t} style={{background:CARD,border:`1px solid ${BORDER}`,borderTop:`2px solid ${r.c}30`,borderRadius:"2px",padding:"12px 14px",background:`linear-gradient(150deg,${r.c}07,${CARD})`}}>
                    <div style={{fontSize:"9px",letterSpacing:"2px",textTransform:"uppercase",color:"#5A5040",whiteSpace:"pre-line",lineHeight:1.5}}>{t}</div>
                    <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"17px",color:r.c,marginTop:"5px"}}>{r.l}</div>
                    <div style={{fontSize:"10px",color:"#4A3A28",marginTop:"2px"}}>{sub}</div>
                  </div>
                );
              })}

              {/* Scenario overlay */}
              <div className="card" style={{padding:"14px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
                  <span style={{fontSize:"11px",fontWeight:600,color:"#E2D8CC"}}>Scenario Overlay</span>
                  <button style={{background:"none",border:"none",color:"#5A5040",cursor:"pointer",fontSize:"13px"}}
                    onClick={()=>{setVolAdj(0);setMfgAdj(0);setFrtAdj(0);setTariffAdj(0);}}>−</button>
                </div>
                {[
                  {l:"Volume Change",       v:volAdj,    s:setVolAdj,    min:-50,max:50},
                  {l:"Mfg Cost Change",     v:mfgAdj,    s:setMfgAdj,    min:-50,max:50},
                  {l:"Freight Change",      v:frtAdj,    s:setFrtAdj,    min:-50,max:50},
                  {l:"Tariff Δ (pts)",      v:tariffAdj, s:setTariffAdj, min:-20,max:30},
                ].map(({l,v,s,min,max})=>(
                  <div key={l} style={{marginBottom:"10px"}}>
                    <div className="sl-hdr">
                      <span className="sl-name">{l}</span>
                      <span className="sl-val">{v>=0?"+":""}{v}{l.includes("pts")?"pts":"%"}</span>
                    </div>
                    <input type="range" min={min} max={max} step={1} value={v} onChange={e=>s(+e.target.value)}/>
                    <div className="sl-scale"><span>{min}%</span><span>0%</span><span>+{max}%</span></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Flags */}
            {flags.length>0 && (
              <div className="flags">
                <div style={{fontSize:"9px",letterSpacing:"2px",textTransform:"uppercase",color:GOLD,marginBottom:"8px",fontWeight:600}}>⚠ Contextual Flags</div>
                {flags.map((f,i)=><div key={i} style={{fontSize:"12px",color:"#C8B88A",marginBottom:"4px"}}>⚠ {f.msg}</div>)}
              </div>
            )}

            {/* Charts */}
            <div style={{display:"grid",gridTemplateColumns:"1.4fr 1fr 1fr",gap:"10px",marginBottom:"10px"}}>
              <div className="card">
                <div className="lbl" style={{marginBottom:"10px"}}>GP by Factory</div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={facBar} barSize={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E1C14"/>
                    <XAxis dataKey="name" tick={{fill:"#5A5040",fontSize:9}}/>
                    <YAxis tick={{fill:"#5A5040",fontSize:9}} tickFormatter={v=>fmt$(v)}/>
                    <Tooltip contentStyle={{background:CARD,border:`1px solid ${BORDER}`,color:"#C8B88A",fontSize:"11px"}} formatter={v=>[fmt$(v),"GP"]}/>
                    <Bar dataKey="gp" radius={[2,2,0,0]}>{facBar.map((_,i)=><Cell key={i} fill={FAC_COLORS[i%FAC_COLORS.length]}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <div className="lbl" style={{marginBottom:"10px"}}>Country Mix (GP %)</div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={cntryPie} cx="50%" cy="44%" innerRadius={34} outerRadius={58} dataKey="value" paddingAngle={3}>
                      {cntryPie.map((_,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                    </Pie>
                    <Tooltip contentStyle={{background:CARD,border:`1px solid ${BORDER}`,color:"#C8B88A",fontSize:"11px"}} formatter={v=>[`${v}%`]}/>
                    <Legend wrapperStyle={{fontFamily:"DM Sans,sans-serif",fontSize:"10px",color:"#6A5A40"}}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <div className="lbl" style={{marginBottom:"10px"}}>Multi-factory SKUs</div>
                <div style={{display:"flex",flexDirection:"column",gap:"4px",marginTop:"4px"}}>
                  {currentSKUs.filter(s=>!s.isSingleFactory).map(s=>(
                    <div key={s.sku_id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid #1A1816`}}>
                      <span style={{fontSize:"12px",color:"#C0B098"}}>{s.sku_name.slice(0,18)}</span>
                      <div style={{display:"flex",gap:"3px"}}>
                        {s.lines.map((l,i)=>(
                          <span key={l.factory} style={{fontSize:"10px",padding:"1px 6px",background:`${FAC_COLORS[allFactories.indexOf(l.factory)%FAC_COLORS.length]}20`,color:FAC_COLORS[allFactories.indexOf(l.factory)%FAC_COLORS.length],borderRadius:"2px"}}>
                            {l.current_allocation_pct}%
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {currentSKUs.filter(s=>!s.isSingleFactory).length===0 && (
                    <div style={{fontSize:"12px",color:"#5A5040",padding:"20px 0",textAlign:"center"}}>All SKUs single-factory</div>
                  )}
                </div>
              </div>
            </div>

            {/* Top SKUs summary table */}
            <div className="card">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
                <div className="sec" style={{marginBottom:0,fontSize:"16px"}}>Top SKUs by GP</div>
                <button className="btn-sm on" onClick={()=>setPage("skus")}>View All SKUs →</button>
              </div>
              <TH cols="2fr 1fr 1fr 1fr 1fr 1fr" ch={<><span>SKU</span><span>Factories</span><span>GP $</span><span>Margin</span><span>Allocation</span><span>Risk</span></>}/>
              {sortedSKUs.slice(0,5).map(s=>{
                const rInfo=ri(skuRisk(s));
                return(
                  <TR key={s.sku_id} cols="2fr 1fr 1fr 1fr 1fr 1fr" ch={<>
                    <div>
                      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"13px",color:"#E2D8CC"}}>{s.sku_name}</div>
                      <div style={{fontSize:"10px",color:"#4A3A28"}}>{s.sku_id} · {s.category}</div>
                    </div>
                    <div>{s.factories.map((f,i)=><span key={f} className="fac-dot" style={{background:FAC_COLORS[allFactories.indexOf(f)%FAC_COLORS.length]}}/>)}<span style={{fontSize:"11px",color:"#5A5040",marginLeft:"4px"}}>{s.factories.join(", ")}</span></div>
                    <span style={{color:"#E2D8CC",fontWeight:500}}>{fmt$(s.totalGP)}</span>
                    <span style={{color:pctColor(s.blendedMargin)}}>{s.blendedMargin.toFixed(1)}%</span>
                    <div className="alloc-bar-wrap" style={{height:"6px",width:"80px"}}>
                      {s.lines.map((l,i)=><div key={l.factory} style={{width:`${l.current_allocation_pct}%`,background:FAC_COLORS[allFactories.indexOf(l.factory)%FAC_COLORS.length]}}/>)}
                    </div>
                    <span><span className="badge" style={{color:rInfo.c,background:`${rInfo.c}18`}}>{rInfo.l}</span></span>
                  </>}/>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SKU DETAIL VIEW ── */}
        {page==="skus" && rawRows.length>0 && (
          <div>
            {hasScenario && (
              <div className="scenario-banner">
                <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                  <span style={{fontSize:"10px",letterSpacing:"2px",textTransform:"uppercase",color:GOLD}}>Scenario Active</span>
                  <span style={{fontSize:"13px",color:gpDelta>=0?GREEN:RED,fontWeight:600}}>{fmtPct(gpDeltaPct)} GP ({fmt$(gpDelta)})</span>
                </div>
                <button className="btn-sm" onClick={()=>{setVolAdj(0);setMfgAdj(0);setFrtAdj(0);setTariffAdj(0);setAllocScenario({});}}>Reset All</button>
              </div>
            )}

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"14px"}}>
              <div className="sec" style={{marginBottom:0}}>SKU Explorer <span style={{fontSize:"13px",color:"#5A5040",fontFamily:"DM Sans,sans-serif",fontWeight:400}}>· {filteredSKUs.length} of {currentSKUs.length}</span></div>
            </div>

            {/* Filters */}
            <div style={{display:"flex",gap:"8px",alignItems:"center",marginBottom:"14px",flexWrap:"wrap"}}>
              <input className="input-f" placeholder="Search SKUs…" value={searchQ} onChange={e=>setSearchQ(e.target.value)} style={{width:"180px"}}/>
              <div style={{display:"flex",gap:"3px"}}>
                {["All",...allCategories].map(c=><button key={c} className={`btn-sm ${filterCat===c?"on":""}`} onClick={()=>setFilterCat(c)}>{c}</button>)}
              </div>
              <div style={{display:"flex",gap:"3px"}}>
                {["All",...allFactories].map(f=><button key={f} className={`btn-sm ${filterFac===f?"on":""}`} onClick={()=>setFilterFac(f)}>{f}</button>)}
              </div>
              <div style={{display:"flex",gap:"3px"}}>
                {["All","Critical","High","Moderate","Low"].map(r=><button key={r} className={`btn-sm ${filterRisk===r?"on":""}`} onClick={()=>setFilterRisk(r)}>{r}</button>)}
              </div>
            </div>

            {/* SKU Cards */}
            {filteredSKUs.map(sku => {
              const rInfo = ri(skuRisk(sku));
              const isExpanded = expandedSku === sku.sku_id;
              const hasAllocChange = !!allocScenario[sku.sku_id];
              const scenSku = scenarioSKUs.find(s=>s.sku_id===sku.sku_id);
              const gpDeltaSku = scenSku ? scenSku.totalGP - currentSKUs.find(s=>s.sku_id===sku.sku_id)?.totalGP : 0;

              return (
                <div key={sku.sku_id} className={`sku-card ${isExpanded?"expanded":""} ${hasAllocChange||hasScenario?"scenario-active":""}`}
                  onClick={()=>setExpandedSku(isExpanded?null:sku.sku_id)}>

                  {/* SKU header row */}
                  <div style={{display:"grid",gridTemplateColumns:"2.5fr 1fr 1fr 1fr 1fr 1fr 1fr 28px",gap:"10px",padding:"12px 16px",alignItems:"center"}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                        <span style={{fontFamily:"'DM Serif Display',serif",fontSize:"14px",color:"#E2D8CC"}}>{sku.sku_name}</span>
                        {!sku.isSingleFactory && <span style={{fontSize:"9px",padding:"1px 6px",background:`${GOLD}18`,color:GOLD,borderRadius:"2px",letterSpacing:"1px"}}>SPLIT</span>}
                        {hasAllocChange && <span style={{fontSize:"9px",padding:"1px 6px",background:`${GREEN}18`,color:GREEN,borderRadius:"2px",letterSpacing:"1px"}}>SCENARIO</span>}
                      </div>
                      <div style={{fontSize:"10px",color:"#4A3A28",marginTop:"1px"}}>{sku.sku_id} · {sku.category}</div>
                    </div>

                    {/* Factories with alloc bar */}
                    <div>
                      <div style={{display:"flex",gap:"3px",marginBottom:"3px"}}>
                        {sku.factories.map((f,i)=>(
                          <span key={f} style={{fontSize:"9px",padding:"1px 6px",background:`${FAC_COLORS[allFactories.indexOf(f)%FAC_COLORS.length]}20`,color:FAC_COLORS[allFactories.indexOf(f)%FAC_COLORS.length],borderRadius:"2px"}}>{f.replace("Factory ","")}</span>
                        ))}
                      </div>
                      <div className="alloc-bar-wrap">
                        {sku.lines.map((l,i)=><div key={l.factory} style={{width:`${l.current_allocation_pct}%`,background:FAC_COLORS[allFactories.indexOf(l.factory)%FAC_COLORS.length]}}/>)}
                      </div>
                    </div>

                    <span style={{fontSize:"13px",color:"#E2D8CC",fontWeight:500}}>{fmt$(sku.totalGP)}</span>
                    <span style={{fontSize:"12px",color:pctColor(sku.blendedMargin)}}>{sku.blendedMargin.toFixed(1)}%</span>
                    <span style={{fontSize:"12px",color:"#C0B098"}}>${sku.blendedLanded.toFixed(0)}</span>
                    <span style={{fontSize:"12px",color:sku.topCountryAlloc>70?RED:sku.topCountryAlloc>40?ORANGE:GREEN}}>{sku.topCountryName} {sku.topCountryAlloc.toFixed(0)}%</span>

                    {(hasAllocChange||hasScenario) ? (
                      <span style={{fontSize:"11px",color:gpDeltaSku>=0?GREEN:RED,fontWeight:600,textAlign:"right"}}>{fmtPct(gpDeltaSku/Math.max(Math.abs(currentSKUs.find(s=>s.sku_id===sku.sku_id)?.totalGP||1),1)*100)}</span>
                    ) : (
                      <span style={{textAlign:"right"}}><span className="badge" style={{color:rInfo.c,background:`${rInfo.c}18`}}>{rInfo.l}</span></span>
                    )}

                    <span style={{color:"#5A5040",fontSize:"12px",textAlign:"center"}}>{isExpanded?"▲":"▼"}</span>
                  </div>

                  {/* Column headers (shown once at top) */}
                  {!isExpanded && false}

                  {/* Expanded panel */}
                  {isExpanded && <SKUPanel sku={sku}/>}
                </div>
              );
            })}

            {/* Column legend */}
            <div style={{display:"grid",gridTemplateColumns:"2.5fr 1fr 1fr 1fr 1fr 1fr 1fr 28px",gap:"10px",padding:"6px 16px",marginBottom:"6px"}}>
              {["SKU","Factories / Alloc","Total GP","Blended Margin","Landed Cost","Top Country %","GP Δ / Risk",""].map((h,i)=>(
                <span key={i} style={{fontSize:"9px",letterSpacing:"1.5px",textTransform:"uppercase",color:"#3A3020"}}>{h}</span>
              ))}
            </div>
          </div>
        )}

        {/* ── AI ── */}
        {page==="ai" && (
          <div>
            <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:"26px",color:"#E2D8CC",marginBottom:"5px"}}>AI Risk <span style={{color:GOLD}}>Analysis</span></h2>
            <p style={{fontSize:"12px",color:"#5A5040",marginBottom:"20px"}}>Executive briefing — covers portfolio risk, allocation inefficiencies, and scenario impacts.</p>
            <div style={{display:"flex",gap:"10px",marginBottom:"20px"}}>
              <button className="btn-g" onClick={generateAI} disabled={aiLoad}>{aiLoad?"Analyzing…":"Generate Analysis"}</button>
              {aiText&&<button className="btn-o" onClick={()=>setAiText("")}>Clear</button>}
            </div>
            {aiLoad&&<div className="card" style={{textAlign:"center",padding:"40px"}}><div style={{fontFamily:"'DM Serif Display',serif",fontSize:"18px",color:GOLD,marginBottom:"6px"}}>Analyzing portfolio…</div><div style={{fontSize:"12px",color:"#5A5040"}}>Evaluating {currentSKUs.length} SKUs across {allFactories.length} factories</div></div>}
            {aiText&&<div><div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"10px"}}><div style={{width:"3px",height:"18px",background:GOLD}}/><div style={{fontSize:"9px",letterSpacing:"3px",color:"#5A5040",textTransform:"uppercase"}}>Blacksmith Intelligence Report · {currentSKUs.length} SKUs · {allFactories.length} Factories</div></div><div className="ai-box">{aiText}</div></div>}
            {!aiText&&!aiLoad&&<div className="card" style={{padding:"40px",textAlign:"center"}}><div style={{fontFamily:"'DM Serif Display',serif",fontSize:"34px",color:"#2A2520",marginBottom:"10px"}}>⬡</div><div style={{fontFamily:"'DM Serif Display',serif",fontSize:"17px",color:"#5A5040"}}>Ready to analyze {currentSKUs.length} SKUs</div></div>}
          </div>
        )}

      </div>
    </div>
  );
}
